import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { identifyByIp, type SpeedtestResult } from "../api/portal";
import { BrandedFooter } from "./BrandedFooter";
import { detectOs, type OS } from "../utils/detectOs";
import { IpGuideAndroid } from "./ip-guides/IpGuideAndroid";
import { IpGuideChromeOs } from "./ip-guides/IpGuideChromeOs";
import { IpGuideIos } from "./ip-guides/IpGuideIos";
import { IpGuideLinux } from "./ip-guides/IpGuideLinux";
import { IpGuideMacos } from "./ip-guides/IpGuideMacos";
import { IpGuideWindows } from "./ip-guides/IpGuideWindows";

// Strict IPv4: each octet 0-255
const IPV4_RE =
  /^(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)\.(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)\.(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)\.(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)$/;

const OS_OPTIONS: { id: OS; label: string }[] = [
  { id: "windows", label: "Windows" },
  { id: "macos", label: "macOS" },
  { id: "ios", label: "iOS" },
  { id: "android", label: "Android" },
  { id: "chromeos", label: "ChromeOS" },
  { id: "linux", label: "Linux" },
];

const IpGuides: Record<OS, React.ComponentType> = {
  ios: IpGuideIos,
  android: IpGuideAndroid,
  windows: IpGuideWindows,
  macos: IpGuideMacos,
  chromeos: IpGuideChromeOs,
  linux: IpGuideLinux,
};

interface DeviceConfirmPageProps {
  portalToken: string;
  siteName: string;
  speedtestResult?: SpeedtestResult;
  matchedHostname?: string | null;
  onConfirmed: (result: SpeedtestResult) => void;
  onSkip: () => void;
}

export function DeviceConfirmPage({
  portalToken,
  siteName,
  speedtestResult,
  matchedHostname,
  onConfirmed,
  onSkip,
}: DeviceConfirmPageProps) {
  const { t } = useTranslation();
  const [guideOpen, setGuideOpen] = useState(false);
  const [os, setOs] = useState<OS>(detectOs());
  const [octets, setOctets] = useState(["", "", "", ""]);
  const [checking, setChecking] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const octetRefs = useRef<Array<HTMLInputElement | null>>([null, null, null, null]);

  const Guide = IpGuides[os];

  const knownIp =
    speedtestResult?.correlation_status === "matched"
      ? speedtestResult.matched_ip
      : null;

  const ipValue = octets.join(".");
  const ipValid = IPV4_RE.test(ipValue);

  // Close modal on Escape
  useEffect(() => {
    if (!guideOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setGuideOpen(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [guideOpen]);

  // Prevent body scroll when modal open
  useEffect(() => {
    document.body.style.overflow = guideOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [guideOpen]);

  function handleOctetChange(idx: number, raw: string) {
    const digits = raw.replace(/\D/g, "").slice(0, 3);
    setOctets((prev) => {
      const next = [...prev];
      next[idx] = digits;
      return next;
    });
    setNotFound(false);
    if (digits.length === 3 && idx < 3) {
      octetRefs.current[idx + 1]?.focus();
      octetRefs.current[idx + 1]?.select();
    }
  }

  function handleOctetKeyDown(idx: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if ((e.key === "." || e.key === ",") && idx < 3) {
      e.preventDefault();
      octetRefs.current[idx + 1]?.focus();
      octetRefs.current[idx + 1]?.select();
    }
    if (e.key === "Backspace" && octets[idx] === "" && idx > 0) {
      e.preventDefault();
      octetRefs.current[idx - 1]?.focus();
    }
    if (e.key === "ArrowRight" && idx < 3) {
      const input = e.currentTarget;
      if (input.selectionStart === input.value.length) {
        e.preventDefault();
        octetRefs.current[idx + 1]?.focus();
      }
    }
    if (e.key === "ArrowLeft" && idx > 0) {
      const input = e.currentTarget;
      if (input.selectionStart === 0 && input.selectionEnd === 0) {
        e.preventDefault();
        octetRefs.current[idx - 1]?.focus();
      }
    }
  }

  function handleOctetPaste(e: React.ClipboardEvent<HTMLInputElement>) {
    const text = e.clipboardData.getData("text").trim();
    const parts = text.split(".");
    if (parts.length === 4 && parts.every((p) => /^\d{1,3}$/.test(p))) {
      e.preventDefault();
      setOctets(parts.map((p) => p.slice(0, 3)));
      setNotFound(false);
      // focus last field after paste
      setTimeout(() => octetRefs.current[3]?.focus(), 0);
    }
  }

  async function handleCheckIp() {
    if (!ipValid) return;
    setChecking(true);
    setNotFound(false);
    try {
      const res = await identifyByIp(portalToken, ipValue);
      if (res.matched && res.speedtest_token) {
        onConfirmed({
          download_mbps: speedtestResult?.download_mbps ?? null,
          upload_mbps: speedtestResult?.upload_mbps ?? null,
          ping_ms: speedtestResult?.ping_ms ?? null,
          jitter_ms: speedtestResult?.jitter_ms ?? null,
          correlation_status: "matched",
          speedtest_token: res.speedtest_token,
          matched_ip: ipValue,
          matched_hostname: res.matched_hostname ?? null,
        });
      } else {
        setNotFound(true);
      }
    } catch {
      setNotFound(true);
    } finally {
      setChecking(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
      <div className="max-w-md mx-auto px-4 py-8">
        <h1 className="text-xl font-semibold text-gray-800 mb-6">{siteName}</h1>

        {knownIp ? (
          /* Szenario A: Speedtest hat gematcht */
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm mb-4">
            <p className="font-semibold text-gray-800 mb-1">{t("ip_confirm_matched_title")}</p>
            <p className="text-sm text-gray-500 mb-4">{t("ip_confirm_matched_desc")}</p>
            {matchedHostname ? (
              <div className="text-center mb-4">
                <p className="text-lg font-semibold text-gray-800">{matchedHostname}</p>
                <p className="text-sm font-mono text-blue-700">{knownIp}</p>
              </div>
            ) : (
              <p className="text-2xl font-mono font-bold text-blue-700 text-center mb-4">{knownIp}</p>
            )}
            <p className="text-sm text-gray-700 text-center mb-6">{t("ip_confirm_question")}</p>
            <button
              type="button"
              onClick={() => onConfirmed(speedtestResult!)}
              className="w-full py-3 px-4 btn-primary rounded-xl font-semibold transition-colors mb-3"
            >
              ✓ {t("ip_confirm_yes")}
            </button>
            <button
              type="button"
              onClick={onSkip}
              className="w-full py-2 px-4 text-gray-500 text-sm hover:text-gray-700 transition-colors"
            >
              → {t("ip_confirm_skip")}
            </button>
          </div>
        ) : (
          /* Szenario B: kein Match, IP manuell eingeben */
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm mb-4">
            <p className="font-semibold text-gray-800 mb-1">{t("ip_confirm_no_match_title")}</p>
            <p className="text-sm text-gray-500 mb-5">{t("ip_confirm_no_match_desc")}</p>

            {/* Segmented IPv4 input */}
            <div className="flex items-center justify-center gap-1 mb-3" role="group" aria-label="IPv4-Adresse">
              {([0, 1, 2, 3] as const).map((idx) => (
                <div key={idx} className="flex items-center gap-1">
                  <input
                    ref={(el) => { octetRefs.current[idx] = el; }}
                    type="text"
                    inputMode="numeric"
                    value={octets[idx]}
                    onChange={(e) => handleOctetChange(idx, e.target.value)}
                    onKeyDown={(e) => handleOctetKeyDown(idx, e)}
                    onPaste={handleOctetPaste}
                    placeholder="0"
                    aria-label={`Oktet ${idx + 1}`}
                    className="w-14 h-12 text-center border border-gray-300 rounded-lg text-lg font-mono focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                  />
                  {idx < 3 && (
                    <span className="text-xl font-bold text-gray-400 select-none">.</span>
                  )}
                </div>
              ))}
            </div>

            {notFound && (
              <p className="text-red-500 text-sm text-center mb-3">{t("ip_confirm_not_found")}</p>
            )}

            <button
              type="button"
              onClick={handleCheckIp}
              disabled={!ipValid || checking}
              className="w-full py-3 px-4 btn-primary rounded-xl font-semibold transition-colors mb-3"
            >
              {checking ? t("ip_confirm_checking") : t("ip_confirm_check_btn")}
            </button>

            <button
              type="button"
              onClick={onSkip}
              className="w-full py-2 px-4 text-gray-500 text-sm hover:text-gray-700 transition-colors"
            >
              → {t("ip_confirm_skip")}
            </button>
          </div>
        )}

        {/* Guide button */}
        <button
          type="button"
          onClick={() => setGuideOpen(true)}
          className="w-full bg-white border border-gray-200 rounded-xl px-5 py-4 text-left text-sm font-medium text-blue-600 hover:bg-gray-50 transition-colors shadow-sm flex items-center justify-between"
        >
          <span>{t("ip_confirm_how_btn")}</span>
          <span className="text-gray-400 text-lg">?</span>
        </button>

        <BrandedFooter />
      </div>

      {/* Guide Modal */}
      {guideOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center"
          onClick={(e) => { if (e.target === e.currentTarget) setGuideOpen(false); }}
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl max-h-[90vh] flex flex-col shadow-2xl">
            {/* Modal header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 shrink-0">
              <h2 className="font-semibold text-gray-800 text-base">{t("ip_confirm_how_btn")}</h2>
              <button
                type="button"
                onClick={() => setGuideOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors text-lg"
                aria-label={t("ip_guide_close")}
              >
                ✕
              </button>
            </div>

            {/* OS selector */}
            <div className="flex gap-2 px-5 pt-4 pb-2 flex-wrap shrink-0">
              {OS_OPTIONS.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => setOs(o.id)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                    os === o.id
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>

            {/* Scrollable guide content */}
            <div className="overflow-y-auto px-5 py-4 flex-1">
              <Guide />
            </div>

            {/* Modal close button at bottom (mobile convenience) */}
            <div className="px-5 pb-5 pt-2 shrink-0 border-t border-gray-100">
              <button
                type="button"
                onClick={() => setGuideOpen(false)}
                className="w-full py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors text-sm"
              >
                {t("ip_guide_close")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
